import {
  Box,
  Input,
  ListItem,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { CaretDown, Prohibit, X } from '@phosphor-icons/react';
import { memo, useState } from 'react';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import { OPERATOR_OPTIONS } from './filterConstants';
import type { Filter } from './types';

interface ListConditionItemProps {
  filter: Filter;
  onOperatorChange: (id: string, operator: string) => void;
  onValueChange: (id: string, value: string) => void;
  onDelete: (id: string) => void;
}

function ListConditionItem({
  filter,
  onOperatorChange,
  onValueChange,
  onDelete,
}: ListConditionItemProps) {
  const [open, setOpen] = useState(false);

  const handleOperatorChange = (e: SelectChangeEvent) => {
    onOperatorChange(filter.id, e.target.value);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(filter.id, e.target.value);
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(open => !open);
  };
  const selectedOperator = OPERATOR_OPTIONS.find(
    op => op.value === filter.operator,
  );
  const isNegated = selectedOperator?.negated || false;

  const getDisplayLabel = () => {
    if (!selectedOperator) return '';
    if (selectedOperator.value === 'not_contains') return 'Contain';
    if (selectedOperator.value === 'not_equals') return 'Equal';
    return selectedOperator.label;
  };

  return (
    <ListItem
      sx={{
        padding: '0.5rem',
        borderRadius: '0.5rem',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#EAECF0',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr min-content',
        gap: '1rem',
      }}
    >
      <Typography
        fontSize='0.875rem'
        height='1.4rem'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.25rem',
          backgroundColor: '#E7F0FF',
          borderRadius: '0.25rem',
          borderColor: '#9AAAFF',
          borderWidth: '1px',
          borderStyle: 'solid',
          textTransform: 'capitalize',
          color: '#3650DE',
        }}
      >
        {filter.label.split('_').join(' ')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          position: 'relative',
        }}
      >
        {isNegated && (
          <Prohibit
            size={16}
            color='#B42318'
            style={{ position: 'absolute', left: '0.5rem', zIndex: 1 }}
          />
        )}
        <Select
          open={open}
          value={filter.operator || ''}
          onClick={handleSelectClick}
          onChange={handleOperatorChange}
          renderValue={() => getDisplayLabel()}
          sx={{
            height: '2rem',
            margin: 0,
            fontSize: '0.875rem',
            width: '100%',
            paddingLeft: isNegated ? '1.75rem' : '0.5rem',
          }}
          MenuProps={{
            disablePortal: false,
            sx: {
              pointerEvents: 'all',
            },
          }}
          IconComponent={CaretDown}
        >
          {OPERATOR_OPTIONS.map(op => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Input
        type='text'
        value={filter.value || ''}
        onChange={handleValueChange}
        placeholder='Value'
        sx={{
          padding: '0.5rem',
          height: '2rem',
          backgroundColor: '#F9FAFB',
          fontSize: '0.875rem',
          color: '#475467',
          borderRadius: '0.25rem',
          borderWidth: '1px',
          borderColor: '#EAECF0',
          borderStyle: 'solid',
          '&::after': { display: 'none' },
          '&::before': { display: 'none' },
        }}
      />

      <IconButtonTooltip
        title='Delete filter'
        onClick={() => onDelete(filter.id)}
      >
        <X size={16} />
      </IconButtonTooltip>
    </ListItem>
  );
}

const MemoizedListConditionItem = memo(ListConditionItem);

export { MemoizedListConditionItem as ListConditionItem };
